import React from "react";
import { toast } from "react-toastify";

import StorageGateway from "lib/storage-gateway";
import PropTypes from "prop-types";
import cookie from "react-cookies";
import connect from "react-redux/lib/connect/connect";
import bindActionCreators from "redux/lib/bindActionCreators";
import * as VendorDucks from "ducks/vendors/vendor";
import * as RouteDucks from "ducks/routes";
import * as UserDucks from "ducks/accounts/user";

import SectionLoaderAtom from "atoms/SectionLoader";
import VendorTableComponent from "components/vendors/list/Table";
import UserAPIGateway from "accounts/gateways/user";

/**
 * VendorListPage -> VendorListSection
 *
 * Components:
 *    - {@link VendorTableComponent}
 *
 * State:
 *    - vendorList
 *
 * Actions:
 *    None
 */

class VendorListSection extends React.Component {
  static async goTo(data, getPermissions, refreshUserData) {
    const userAPIGateway = new UserAPIGateway();
    let domain = window.location.hostname;
    const host = window.location.hostname.split(".");
    if (host[0].indexOf("admin") >= 0) domain = `.${host[1]}.${host[2]}`;

    try {
      StorageGateway.set("company_id", data.id);
      cookie.save("company_id", data.id, { path: "/", domain });
      cookie.remove("payrollId", { path: "/" });
      cookie.remove("startDate", { path: "/" });
      cookie.remove("endDate", { path: "/" });
      cookie.remove("departmentId", { path: "/" });
      cookie.remove("teamId", { path: "/" });

      if (refreshUserData) {
        console.log("🔍 Refreshing user data after company change");
        await refreshUserData();
        await new Promise((resolve) => setTimeout(resolve, 500));
        const jobTrackAccess = await userAPIGateway.getJobTrackToken();


        if (jobTrackAccess) {
          StorageGateway.set("jobTrackOrgId", jobTrackAccess?.organisation_id ?? null);
          StorageGateway.set("JobTrackBearerToken", jobTrackAccess?.access_token ?? null);
          StorageGateway.set("iframe_jwt_token", jobTrackAccess?.jwt_token ?? null);
          StorageGateway.set("iframe_jwt_token_expiry", jobTrackAccess?.expires_in ?? null);
          //  First define login time
            const loginTime = Date.now();
            // Convert into string
            const loginTimeStr = loginTime.toString();    
          
            // Pass into storage
            StorageGateway.set("loginTimestamp", loginTimeStr);
          
        }
      }
    } catch (error) {
      if (
        error.name === "GraniteError" ||
        error.constructor.name === "GraniteError"
      ) {
        console.error(
          "Bad Request (GraniteError): ",
          error.message || error.errors
        );
        StorageGateway.remove("jobTrackOrgId");
        StorageGateway.remove("JobTrackBearerToken");
        StorageGateway.remove("iframe_jwt_token");
        StorageGateway.remove("jobTrackRefreshToken");
      } else {
        console.error("Unexpected error: ", error);
      }
    } finally {
      setTimeout(() => {
        window.location.href = "/dashboard";
      });
    }
  }

  componentDidMount() {
    // If userExtraData is empty, fetch it
    if (!this.props.userExtraData || !this.props.userExtraData.company) {
      console.log("🔍 Fetching extra data for VendorListSection");
      this.props.UserDucks.getExtraData();
    }
  }

  constructor(props) {
    super(props);
    this.updateSelected = this.updateSelected.bind(this);
    this.getStats = this.getStats.bind(this);
    this.approvePlanChange = this.approvePlanChange.bind(this);
    this.rejectPlanChange = this.rejectPlanChange.bind(this);
  }

  approvePlanChange(id) {
    const {
      location: { query },
    } = this.props;
    let detail;
    detail = { ...query };
    const pageSize = cookie.load("vendor_page_size");
    return this.props.VendorDucks.planChange(id)
      .then(() => {
        this.props.VendorDucks.getVendors({ ...detail, page_size: pageSize });
      })
      .catch(() => {
        toast.error("Cannot approve cancellation since payment is due");
      });
  }

  rejectPlanChange(id) {
    const {
      location: { query },
    } = this.props;
    let detail;
    detail = { ...query };
    const pageSize = cookie.load("vendor_page_size");
    const data = { company_id: id, in_progress: false };
    return this.props.VendorDucks.planReject(data)
      .then(() => {
        this.props.VendorDucks.getVendors({ ...detail, page_size: pageSize });
      })
      .catch(() => {
        this.props.VendorDucks.getVendors({ ...detail, page_size: pageSize });
      });
  }

  getStats(data) {
    return this.props.VendorDucks.getStats(data);
  }

  updateSelected(data) {
    return this.props.VendorDucks.selectVendors(data);
  }

  render() {
    const {
      isLoading,
      vendorList,
      setAttributeToggler,
      attributeToggler,
      companyPermissions,
    } = this.props;

    let newVendorList = vendorList;

    if (isLoading) return <SectionLoaderAtom active />;
    return (
      <section className="pl-3 pr-4">
        <VendorTableComponent
          location={this.props.location}
          data={newVendorList}
          goTo={(data) =>
            this.constructor.goTo(
              data,
              () => this.props.companyPermissions, // Pass function to get current permissions
              this.props.UserDucks.getExtraData
            )
          }
          updateSelected={this.updateSelected}
          getStats={this.getStats}
          approvePlanChange={this.approvePlanChange}
          rejectPlanChange={this.rejectPlanChange}
          setAttributeToggler={setAttributeToggler}
          attributeToggler={attributeToggler}
        />
      </section>
    );
  }
}

const mapStateToProps = (state) => {
  const permissions = UserDucks.companyPermissions(state);
  const userExtraData = UserDucks.userExtraCompanyData(state);
  const currentCompanyId = userExtraData?.id;
  return {
    location: RouteDucks.location(state),
    vendorList: VendorDucks.vendorList(state),
    companyPermissions: permissions,
  };
};

const mapActionsToProps = (dispatch) => ({
  VendorDucks: bindActionCreators(VendorDucks, dispatch),
  UserDucks: bindActionCreators(UserDucks, dispatch),
});

VendorListSection.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  vendorList: PropTypes.object.isRequired,
  companyPermissions: PropTypes.array,
  UserDucks: PropTypes.object,
};

export default connect(mapStateToProps, mapActionsToProps)(VendorListSection);
